import type { SnowplowContextName } from '@/helpers/tracking/providers';

import { Pinia } from 'pinia';
import Cookies from 'js-cookie';

import { CONTEXT_CLASSES } from '@/helpers/tracking/providers';
import { captureMessageForSentry } from '@/libraries/sentry';

import { STORE_NAMESPACE } from '@/stores/cookieStorage';
import { useUserStore } from '@/stores/pinia/useUserStore';

/** Pinia Persisted State Plugin namespace à la jw/user. */
export default defineNuxtPlugin({
	name: 'pinia-init',
	setup(nuxtApp) {
		const pinia = nuxtApp.$pinia as Pinia;
		initPiniaStore(pinia);
	},
});

async function initPiniaStore(pinia: Pinia) {
	if (import.meta.server) {
		server_initPiniaStore(pinia);
	}

	if (import.meta.client) {
		client_initPiniaStore(pinia);
	}
}

function server_initPiniaStore(pinia: Pinia) {
	const savedState: any = {};

	const userCookieRaw = useCookie<Record<string, any> | undefined>(`${STORE_NAMESPACE}_user`);
	const userCookieFallbackRaw = useCookie<Record<string, any> | undefined>(`${STORE_NAMESPACE}/user`);

	const userCookie = userCookieRaw.value || userCookieFallbackRaw.value;
	if (userCookie) {
		try {
			savedState.user = typeof userCookie === 'string' ? JSON.parse(userCookie) : userCookie;
		} catch {
			console.error(`[piniaStore.ts] createStore error parsing ${STORE_NAMESPACE}_user`, userCookie);
		}
	}

	const accessTokenCookie = useCookie<string | undefined>('access_token');
	const token = accessTokenCookie.value;

	if (token) {
		(savedState.user ??= {}).accessToken = token;
	}

	if (savedState.user) {
		useUserStore(pinia).$patch(savedState.user);
	}
}

function client_initPiniaStore(pinia: Pinia) {
	// Cookies are the source of truth for auth state across all domains
	const accessTokenCookie = Cookies.get('access_token');

	const userCookie = Cookies.get(`${STORE_NAMESPACE}_user`) || Cookies.get(`${STORE_NAMESPACE}/user`);
	let parsedCookieUser = null;
	try {
		parsedCookieUser = JSON.parse(userCookie ?? 'null');
	} catch (err) {
		captureMessageForSentry(
			'[piniaStore.ts] Error parsing user cookie',
			{ err, where: 'piniaStore.ts client_createPiniaStore (cookie parsing)' },
			'error'
		);
	}

	const cookieHasAuth = parsedCookieUser != null && parsedCookieUser.jwLoginId != null && accessTokenCookie != null;

	// localStorage is not cross domain, changing auth state in domain then moving
	// to the other will leave local storage auth info out of sync with the cookie.
	let savedUser: Record<string, any> = {};
	try {
		savedUser = JSON.parse(localStorage.getItem(`${STORE_NAMESPACE}/user`) ?? 'null') ?? {};
	} catch (err) {
		captureMessageForSentry(
			'[piniaStore.ts] Error parsing saved user from localStorage',
			{ err, where: 'piniaStore.ts client_createPiniaStore (localStorage parsing)' },
			'error'
		);
	}

	const localStorageHasAuth = savedUser.jwLoginId != null && savedUser.accessToken != null;

	const serverSentState = pinia.state.value.user ?? {};

	// Snapshot window.__DATA__.piniaState.user immediately before anything can modify it
	if (serverSentState?.user) {
		// Deep clone just the user state to preserve original SSR state
		(window as any).__INITIAL_SSR_USER__ = serverSentState?.user ?? {};
		// check if jw_user exist in document.cookie
		(window as any).__INITIAL_USER_COOKIE_AVAILABILITY__ = !!document.cookie
			.split('; ')
			.some(cookie => cookie.startsWith('jw_user='));
		(window as any).__INITIAL_USER_LOCALSTORAGE_AVAILABILITY__ = !!localStorage.getItem('jw/user');
	}

	let didSyncFromCookies = false;

	if (cookieHasAuth !== localStorageHasAuth) {
		// Cookie says logged in, localStorage says logged out
		// 	→ Apply login state to localStorage from Cookie
		if (cookieHasAuth) {
			savedUser = mergeCookieAuthIntoLocalStorageUser(parsedCookieUser, accessTokenCookie, savedUser);
		}

		// Cookie says logged out, localStorage says logged in
		// 	→ Apply logout state to localStorage
		if (localStorageHasAuth) {
			savedUser = clearAuthStateFromLocalStorage(savedUser, parsedCookieUser);
		}

		localStorage.setItem(`${STORE_NAMESPACE}/user`, JSON.stringify(savedUser));
		didSyncFromCookies = true;
	}

	// Check if server auth matches cookie auth
	let shouldIgnoreServerUserState = false;
	if (didSyncFromCookies) {
		const serverStateHasAuth = serverSentState.user?.jwLoginId != null && serverSentState.user?.accessToken != null;
		shouldIgnoreServerUserState = cookieHasAuth !== serverStateHasAuth;
	}

	const baseUser = !shouldIgnoreServerUserState ? serverSentState.user || {} : {};

	const isBaseDataEmpty = Object.keys(baseUser).length === 0;
	const serverOverrides = isBaseDataEmpty
		? {}
		: {
				settingsProvider: baseUser.settingsProvider,
				preferences: baseUser.preferences,
				newBuyboxFilterType: baseUser.newBuyboxFilterType,
				newBuyboxView: baseUser.newBuyboxView,
				buyboxExpView: baseUser.buyboxExpView,
		  };

	savedUser = {
		...savedUser,
		...serverOverrides,
	};

	// SAVE INITIAL USER STATE
	(window as any).__INITIAL_SAVED_USER_STATE__ = JSON.parse(JSON.stringify(savedUser));

	serverSentState.user = {
		...baseUser,
		...savedUser,
	};
	useUserStore(pinia).$patch(serverSentState.user);
	// Reconstruct SnowplowContext class instances from serialized state
	const trackingState = pinia.state.value.tracking;
	if (trackingState?.queues) {
		for (const queueType of Object.keys(trackingState.queues)) {
			trackingState.queues[queueType] = (trackingState.queues[queueType] || []).map((item: any) => ({
				...item,
				contexts: (item.contexts || []).map((serializedContext: { __name: SnowplowContextName }) => {
					const ContextClass = CONTEXT_CLASSES[serializedContext.__name];
					if (!ContextClass) return serializedContext;

					return Object.create(ContextClass.prototype, Object.getOwnPropertyDescriptors(serializedContext));
				}),
			}));
		}
	}
}

function mergeCookieAuthIntoLocalStorageUser(cookieUser: any, accessTokenCookie: string, localStorageUser: any) {
	// Start with cookie data as base (has all necessary fields including jwId)
	// Then merge in localStorage preferences to preserve user customizations
	// Note: Only include properties that are actually persisted to cookies (see persist-state.plugin.ts)
	const authFieldsFromCookie = {
		jwId: cookieUser.jwId,
		jwLoginId: cookieUser.jwLoginId,
		accessToken: accessTokenCookie,
		loggedInProviders: cookieUser.loggedInProviders ?? [],
		isPremium: cookieUser.isPremium,
		userRole: cookieUser.userRole,
		onboardingStatus: cookieUser.onboardingStatus,
		lastUsedLoginProvider: cookieUser.lastUsedLoginProvider,
		accountCreationTime: cookieUser.accountCreationTime,
	};

	// Merge: cookie as base, then layer localStorage on top, then auth fields override
	return {
		// Base: all fields from cookie
		...cookieUser,

		// Override: preferences from localStorage
		...localStorageUser,

		// Final override: auth fields from cookie
		...authFieldsFromCookie,
	};
}

// Preserve existing user object, only clear auth-related fields
function clearAuthStateFromLocalStorage(localStorageUser: any, cookieUser: any) {
	return {
		...localStorageUser,
		jwLoginId: undefined,
		accessToken: undefined,
		loggedInProviders: [],
		isPremium: false,
		userRole: 'USER',
		info: {},
		profileInfo: null,
		onboardingStatus: 'GLOBAL',
		lastUsedLoginProvider: cookieUser?.lastUsedLoginProvider || '',
		accountCreationTime: null,
		profileIsComplete: false,
	};
}
